import {
    Questionnaire,
    QuestionnaireChoice,
    QuestionnaireChoiceDescription,
    QuestionnaireChoices,
    QuestionnaireItem,
} from "@patient-watch/ui/questionnaire";
import { type FormEvent, type KeyboardEvent, type ReactNode, useEffect, useRef } from "react";
import { optionIndexFromDigitKey } from "./choiceDigitKey";

export interface ChoiceLine {
    label: ReactNode;
    description?: ReactNode;
}

export interface ChoiceGroupOption {
    value: string;
    label: ReactNode;
    /** Muted line under the choice label. */
    description?: ReactNode;
    /** Further heading and detail rows in the same choice. */
    segments?: ChoiceLine[];
    /** Shown but not selectable, for example a dictionary option marked is_disabled. */
    disabled?: boolean;
}

function choiceLines(option: ChoiceGroupOption): ChoiceLine[] {
    return [{ label: option.label, description: option.description }, ...(option.segments ?? [])];
}

function hasChoiceDetail(lines: ChoiceLine[]): boolean {
    return lines.some((line) => line.description != null && line.description !== "");
}

function ChoiceOptionContent({ option }: { option: ChoiceGroupOption }) {
    const lines = choiceLines(option);
    if (!hasChoiceDetail(lines)) return <>{option.label}</>;

    return (
        <>
            {lines.map((line, index) => (
                <span key={index} className={index > 0 ? "flex flex-col gap-0.5 mt-1.5" : "flex flex-col gap-0.5"}>
                    <span className="font-medium">{line.label}</span>
                    {line.description ? (
                        <QuestionnaireChoiceDescription>{line.description}</QuestionnaireChoiceDescription>
                    ) : null}
                </span>
            ))}
        </>
    );
}

interface Props {
    /** The input id. Becomes the native field name, so it must be unique on the page. */
    name: string;
    options: ChoiceGroupOption[];
    /** Radios when false, checkboxes when true. */
    multiple?: boolean;
    /** The chosen value, or the list of chosen values when `multiple`. */
    value: string | string[] | undefined | null;
    onChange: (value: string | string[]) => void;
    invalid?: boolean;
    /** Id of the element that labels the group. */
    labelledBy?: string;
    describedBy?: string;
    disabled?: boolean;
    /**
     * Each radio is its own tab stop. A shared name would let Tab land on only one answer.
     * Defaults on for single choice. Checkboxes are already separate tab stops.
     */
    tabEachOption?: boolean;
    "data-testid"?: string;
}

/** Text entry. Radios and checkboxes stay eligible for digit shortcuts and Enter-to-advance. */
function isTypingTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    if (target.isContentEditable) return true;
    if (target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return true;
    if (!(target instanceof HTMLInputElement)) return false;
    const type = target.type;
    return type !== "radio" && type !== "checkbox" && type !== "button" && type !== "submit" && type !== "reset";
}

function isSubmitControl(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    if (target instanceof HTMLInputElement && target.type === "submit") return true;
    if (!(target instanceof HTMLButtonElement)) return false;
    return target.type === "submit";
}

/** The questionnaire primitive is its own form. Advance the page form around it. */
function submitPageForm(group: HTMLElement): void {
    const inner = group.querySelector("form");
    const page = (inner?.parentElement ?? group).closest("form");
    if (!(page instanceof HTMLFormElement)) return;

    const submitter = page.querySelector<HTMLButtonElement | HTMLInputElement>(
        'button[type="submit"]:not([disabled]), input[type="submit"]:not([disabled])'
    );
    if (submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement) {
        page.requestSubmit(submitter);
        return;
    }

    if (page.querySelector('button[type="submit"], input[type="submit"]')) return;
    page.requestSubmit();
}

/**
 * Choices drawn with the shadcn Questionnaire primitive: native radios or checkboxes, whole-row
 * hit area, arrow-key movement between answers.
 *
 * The answer lives in react-hook-form, not in the primitive, so every choice is controlled.
 * The primitive renders its own `<form>` inside the page form. Its submit and reset stay here.
 * Enter on a selected radio advances the page form (the same as Next). Selecting a radio does not.
 *
 * Label-only radios (no number on the control) accept digit keys: 0 is the first option from the
 * top. Numbered scale circles keep their own shortcuts and are not handled here.
 */
export function ChoiceGroup({
    name,
    options,
    multiple = false,
    value,
    onChange,
    invalid,
    labelledBy,
    describedBy,
    disabled,
    tabEachOption,
    "data-testid": testId,
}: Props) {
    const eachTabStop = tabEachOption ?? !multiple;
    const groupRef = useRef<HTMLDivElement>(null);
    const optionsRef = useRef(options);
    const onChangeRef = useRef(onChange);
    const disabledRef = useRef(disabled);
    optionsRef.current = options;
    onChangeRef.current = onChange;
    disabledRef.current = disabled;

    useEffect(() => {
        if (multiple) return;

        const handleKeyDown = (event: globalThis.KeyboardEvent) => {
            if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
            if (event.repeat) return;
            if (isTypingTarget(event.target)) return;

            const group = groupRef.current;
            if (!group) return;

            const active = document.activeElement;
            if (active instanceof Element && active.closest("[data-rating-keyboard]")) return;

            const scope = group.closest<HTMLElement>("[data-choice-question]") ?? group;
            const focusedHere = active instanceof Node && scope.contains(active);
            const groups = document.querySelectorAll("[data-choice-keyboard]");
            if (groups.length > 1 && !focusedHere) return;
            if (!focusedHere && document.querySelector("[data-rating-keyboard]")) return;

            if (event.key === "Enter") {
                if (!focusedHere || isSubmitControl(event.target)) return;
                if (!group.querySelector('input[type="radio"]:checked')) return;
                event.preventDefault();
                event.stopPropagation();
                submitPageForm(group);
                return;
            }

            const index = optionIndexFromDigitKey(event.key, optionsRef.current.length);
            if (index === null) return;

            const option = optionsRef.current[index];
            if (!option || option.disabled || disabledRef.current) return;

            event.preventDefault();
            event.stopPropagation();
            onChangeRef.current(option.value);
        };

        window.addEventListener("keydown", handleKeyDown, true);
        return () => window.removeEventListener("keydown", handleKeyDown, true);
    }, [multiple]);

    const chosen = Array.isArray(value) ? value : value == null || value === "" ? [] : [String(value)];

    function stop(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        event.stopPropagation();
    }

    function moveByArrow(event: KeyboardEvent<HTMLDivElement>) {
        if (!eachTabStop) return;
        const forward = event.key === "ArrowDown" || event.key === "ArrowRight";
        const backward = event.key === "ArrowUp" || event.key === "ArrowLeft";
        if (!forward && !backward) return;
        if (!(event.target instanceof HTMLInputElement) || event.target.type !== "radio") return;

        const radios = groupRef.current?.querySelectorAll<HTMLInputElement>('input[type="radio"]:not(:disabled)');
        if (!radios?.length) return;
        const list = [...radios];
        const index = list.indexOf(event.target);
        if (index < 0) return;

        event.preventDefault();
        const next = list[(index + (forward ? 1 : -1) + list.length) % list.length];
        next.focus();
        next.click();
    }

    function toggle(optionValue: string, checked: boolean) {
        if (!multiple) {
            onChange(optionValue);
            return;
        }
        onChange(
            checked
                ? [...chosen.filter((v) => v !== optionValue), optionValue]
                : chosen.filter((v) => v !== optionValue)
        );
    }

    return (
        <div
            ref={groupRef}
            data-choice-keyboard={multiple ? undefined : ""}
            className="w-full min-w-0"
            onKeyDown={moveByArrow}
        >
            <Questionnaire onSubmit={stop} onReset={stop}>
                <QuestionnaireItem
                    name={name}
                    multiple={multiple}
                    invalid={invalid}
                    aria-labelledby={labelledBy}
                    aria-describedby={describedBy}
                >
                    <QuestionnaireChoices data-testid={testId}>
                        {options.map((option) => {
                            const checked = chosen.includes(option.value);
                            return (
                                <QuestionnaireChoice
                                    key={option.value}
                                    value={option.value}
                                    checked={checked}
                                    disabled={disabled || option.disabled}
                                    onChange={(event) => toggle(option.value, event.target.checked)}
                                    className={hasChoiceDetail(choiceLines(option)) ? "items-start" : undefined}
                                    data-testid="form-option"
                                    data-input-id={name}
                                    data-option-value={option.value}
                                    inputProps={
                                        {
                                            "data-testid": `form-option-${name}-${option.value}`,
                                            ...(eachTabStop ? { name: `${name}-${option.value}`, tabIndex: 0 } : {}),
                                        } as never
                                    }
                                >
                                    <ChoiceOptionContent option={option} />
                                </QuestionnaireChoice>
                            );
                        })}
                    </QuestionnaireChoices>
                </QuestionnaireItem>
            </Questionnaire>
        </div>
    );
}
